import React from 'react';
import { Card, CardContent, Typography, TextField, Box, Button, IconButton, Stack, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { BillingItemData } from '@app-types/visit';
import { Receipt, Add, Delete } from '@mui/icons-material';

interface BillingCardProps {
  billingItems: BillingItemData[];
  onChange: (items: BillingItemData[]) => void;
  disabled?: boolean;
}

const BillingCard: React.FC<BillingCardProps> = React.memo(({ billingItems, onChange, disabled = false }) => {
  const addItem = () => {
    const newItem: BillingItemData = {
      id: `bill-${Date.now()}`,
      name: '',
      qty: 1,
      uom: 'buc',
      unitPrice: 0,
      total: 0,
      fromStock: false
    };
    onChange([...billingItems, newItem]);
  };

  const updateItem = (id: string, updates: Partial<BillingItemData>) => {
    onChange(billingItems.map(item => {
      if (item.id === id) {
        const merged = { ...item, ...updates };
        // Recalculate total
        merged.total = merged.qty * merged.unitPrice;
        return merged;
      }
      return item;
    }));
  };

  const deleteItem = (id: string) => {
    onChange(billingItems.filter(item => item.id !== id));
  };

  const calculateTotal = () => {
    return billingItems.reduce((sum, item) => sum + item.total, 0);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Receipt sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="h6">Consumabile & Servicii</Typography>
          </Box>
          <Button
            startIcon={<Add />}
            onClick={addItem}
            disabled={disabled}
            size="small"
            variant="outlined"
          >
            Adaugă linie
          </Button>
        </Box>
        
        <Stack spacing={2}>
          {billingItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Nu există linii de facturare. Apasă "Adaugă linie" pentru a începe.
            </Typography>
          )}
          
          {billingItems.map((item) => (
            <Box key={item.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
<Grid container spacing={2} alignItems="center">
<Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    required
                    label="Nume articol/serviciu"
                    value={item.name}
                    onChange={(e) => updateItem(item.id, { name: e.target.value })}
                    disabled={disabled}
                    error={!item.name}
                  />
                </Grid>
                
<Grid size={{ xs: 4, md: 2 }}>
                  <TextField
                    fullWidth
                    type="number"
                    required
                    label="Cantitate"
                    value={item.qty}
                    onChange={(e) => updateItem(item.id, { qty: Number(e.target.value) })}
                    disabled={disabled}
                    inputProps={{ min: 0.01, step: 0.1 }}
                    error={item.qty <= 0}
                  />
                </Grid>
                
<Grid size={{ xs: 4, md: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="UOM"
                    value={item.uom}
                    onChange={(e) => updateItem(item.id, { uom: e.target.value })}
                    disabled={disabled}
                    placeholder="buc, ml, kg"
                    error={!item.uom}
                  />
                </Grid>
                
<Grid size={{ xs: 4, md: 2 }}>
                  <TextField
                    fullWidth
                    type="number"
                    required
                    label="Preț unitar (RON)"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(item.id, { unitPrice: Number(e.target.value) })}
                    disabled={disabled}
                    inputProps={{ min: 0, step: 0.01 }}
                    error={item.unitPrice < 0}
                  />
                </Grid>
                
<Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    label="Total (RON)"
                    value={item.total.toFixed(2)}
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                
<Grid size={{ xs: 6, md: 1 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    color="error"
                    onClick={() => deleteItem(item.id)}
                    disabled={disabled}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Stack>
        
        {billingItems.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ mr: 2 }}>
                Total:
              </Typography>
              <Typography variant="h5" color="success.main" fontWeight="bold">
                {calculateTotal().toFixed(2)} RON
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
});

BillingCard.displayName = 'BillingCard';

export default BillingCard;
